import {
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { isValidElement } from "react";
import { StatusRow } from "./StatusRow";
import { isMobile } from "react-device-detect";
import { TableAction, TableStyles, TableColumn } from "./types";
import { TableActionButton } from "./TableActionButton";
import {
  tableRowStyles,
  tableDataCellStyles,
  tableActionCellStyles,
  tableActionContentStyles,
} from "./styles";

interface GenericTableBodyProps<T = any> {
  rows: T[];
  startIndex: number;
  endIndex: number;
  rowPath: string[];
  actions?: TableAction<T>[] | ((row: T) => React.ReactNode);
  characterLimit: number;
  styles?: TableStyles;
  columns?: TableColumn[];
  onRowClick?: (row: T) => void;
  selectionCellContent?: (row: T) => React.ReactNode;
  statusVariant?: "default" | "questionnaire";
}

export function GenericTableBody<T extends Record<string, any>>({
  rows,
  startIndex,
  endIndex,
  rowPath,
  actions,
  characterLimit,
  styles,
  columns,
  onRowClick,
  selectionCellContent,
  statusVariant,
}: GenericTableBodyProps<T>) {
  const theme = useTheme();

  /**
   * Compatibilidade: vários componentes de ações no projeto retornam `<TableCell>`.
   * Se vier um TableCell embrulhado, extraímos apenas seus filhos para evitar `td` aninhado.
   */
  const unwrapMuiTableCell = (
    node: React.ReactNode
  ): { content: React.ReactNode; sx?: any } => {
    if (Array.isArray(node) && node.length === 1) {
      return unwrapMuiTableCell(node[0]);
    }
    if (!isValidElement(node)) return { content: node };
    if (node.type === React.Fragment) {
      return unwrapMuiTableCell((node as any).props?.children);
    }
    const anyType: any = node.type as any;
    const isMuiTableCell =
      anyType === TableCell || anyType?.muiName === "MuiTableCell";
    if (!isMuiTableCell) return { content: node };
    return { content: (node as any).props?.children, sx: (node as any).props?.sx };
  };

  /**
   * Renderiza o conteúdo da célula com clamp visual e tooltip no hover.
   * - lineClamp 1 → linha única, text-overflow: ellipsis
   * - lineClamp 2+ (padrão) → múltiplas linhas com -webkit-line-clamp
   * - Tooltip sempre exibe o texto completo ao passar o mouse
   */
  const renderCell = (
    content: any,
    isFirst: boolean,
    align: "left" | "center" | "right" = "left",
    lineClamp: number = 2,
  ) => {
    if (!content) {
      return (
        <Typography
          variant="body2"
          color="text.disabled"
          fontSize="0.8125rem"
          textAlign={align}
          display="block"
        >
          —
        </Typography>
      );
    }

    /* Elemento React (ex: Stack com telefone + WhatsApp) — renderiza direto sem clamp/tooltip */
    if (isValidElement(content)) {
      return <>{content}</>;
    }

    const text = content?.[0]?.props?.children || content;
    /* Garante que o tooltip sempre receba uma string legível, nunca [object Object] */
    const str = typeof text === "string" || typeof text === "number" ? String(text) : "";

    /*
     * Estilos de clamp:
     * lineClamp = 0 → sem limite, texto quebra livremente
     * lineClamp = 1 → linha única com text-overflow: ellipsis
     * lineClamp > 1 → múltiplas linhas com -webkit-line-clamp
     */
    /*
     * Clamp via style inline (bypassa o emotion) para garantir que os
     * vendor prefixes cheguem ao DOM exatamente como escritos.
     * O span receptor deve conter o texto diretamente (sem filhos block).
     */
    const clampStyle: React.CSSProperties =
      lineClamp === 0
        ? { wordBreak: "break-word", overflowWrap: "anywhere" }
        : lineClamp === 1
        ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
        : {
            display: "-webkit-box",
            WebkitLineClamp: lineClamp,
            /* Propriedade obrigatória para o clamp funcionar */
            WebkitBoxOrient: "vertical" as React.CSSProperties["WebkitBoxOrient"],
            overflow: "hidden",
          };

    return (
      /* Tooltip sempre ativo — exibe texto completo no hover */
      <Tooltip title={str} placement="top" arrow>
        {/*
         * O clamp é aplicado diretamente no elemento que contém o texto.
         * -webkit-line-clamp requer que o texto seja filho direto do elemento
         * com display:-webkit-box para funcionar corretamente.
         */}
        <span
          style={{
            display: "block",
            width: "100%",
            fontSize: "0.8125rem",
            fontWeight: isFirst ? 600 : 400,
            lineHeight: 1.4,
            cursor: "default",
            textAlign: align as any,
            ...clampStyle,
          }}
        >
          {text}
        </span>
      </Tooltip>
    );
  };

  /** Coluna de ações — suporta função legada e array moderno */
  const renderActions = (row: T) => {
    if (!actions) return null;

    // Formato legado: função que retorna ReactNode
    if (typeof actions === "function") {
      const raw = actions(row);
      if (!raw) return null;
      const { content, sx } = unwrapMuiTableCell(raw);
      if (!content) return null;
      return (
        <TableCell
          sx={{
            ...tableActionCellStyles,
            ...(styles?.actionCell || {}),
            ...(sx || {}),
          }}
        >
          <Box sx={tableActionContentStyles}>{content}</Box>
        </TableCell>
      );
    }

    // Novo formato: array de TableAction
    const visibleActions = actions.filter(
      (action) => !action.isVisible || action.isVisible(row)
    );
    if (!visibleActions.length) return null;

    return (
      <TableCell
        sx={{
          ...tableActionCellStyles,
          ...(styles?.actionCell || {}),
        }}
      >
        <Box sx={tableActionContentStyles}>
          {visibleActions.map((action, index) => {
            const disabled = action.isDisabled?.(row);
            return (
              <TableActionButton
                key={index}
                compact
                icon={action.icon as React.ReactElement}
                label={action.tooltip || "Ação"}
                tone="primary"
                disabled={disabled}
                onClick={() => action.onClick(row)}
              />
            );
          })}
        </Box>
      </TableCell>
    );
  };

  const hasSelection = typeof selectionCellContent === "function";
  const isInactive = (row: T) => row.ends === "Inativo";
  const isProposed = (row: T) => row.status === "proposed";

  const rowCellSx = (row: T, cellIndex: number) => {
    const align = columns?.[cellIndex]?.align || "left";
    return {
      ...tableDataCellStyles,
      textAlign: align,
      ...(styles?.cell || {}),
      /* Linha inativa: riscado + opacidade reduzida */
      textDecoration: isInactive(row) ? "line-through" : "none",
      opacity: isInactive(row) ? 0.55 : 1,
    };
  };

  /** Células de dados — separado para evitar duplicação mobile/desktop */
  const dataCells = (row: T) =>
    rowPath?.map((path, cellIndex) => {
      const col = columns?.[cellIndex];
      const align = col?.align || "left";
      const lineClamp = col?.lineClamp ?? 2;
      return (
        <TableCell
          key={`tableBodyCell-${cellIndex}`}
          align={align}
          sx={rowCellSx(row, cellIndex)}
        >
          {path === "status" ? (
            <StatusRow status={row?.[path]} variant={statusVariant} />
          ) : (
            renderCell(row[path], cellIndex === 0, align, lineClamp)
          )}
        </TableCell>
      );
    });

  return (
    <TableBody>
      {rows?.slice(startIndex, endIndex).map((row, index) => (
        <TableRow
          key={index}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={{
            ...tableRowStyles,
            ...(styles?.row || {}),
            /* Fundo de acordo com o estado da linha */
            backgroundColor: isInactive(row)
              ? alpha(theme.palette.action.disabledBackground, 0.4)
              : isProposed(row)
              ? "warning100.main"
              : row.isSelected
              ? alpha(theme.palette.primary.main, 0.06)
              : "inherit",
            /* Borda esquerda colorida para linhas pendentes/selecionadas */
            borderLeft: isProposed(row) || row.isSelected ? "3px solid" : "3px solid transparent",
            borderLeftColor: isProposed(row)
              ? "warning.main"
              : row.isSelected
              ? "primary.main"
              : "transparent",
            cursor: onRowClick ? "pointer" : "default",
            transition: "background-color 0.14s ease, border-left-color 0.14s ease",
            "&:hover": {
              backgroundColor: isProposed(row)
                ? "warning200.main"
                : alpha(theme.palette.primary.main, 0.03),
              borderLeftColor: isProposed(row)
                ? "warning.main"
                : alpha(theme.palette.primary.main, 0.25),
            },
          }}
        >
          {isMobile ? (
            <>
              {hasSelection && (
                <TableCell padding="checkbox" align="center" sx={{ ...tableDataCellStyles, ...(styles?.cell || {}) }}>
                  {selectionCellContent!(row)}
                </TableCell>
              )}
              {renderActions(row)}
              {dataCells(row)}
            </>
          ) : (
            <>
              {hasSelection && (
                <TableCell padding="checkbox" align="center" sx={{ ...tableDataCellStyles, ...(styles?.cell || {}) }}>
                  {selectionCellContent!(row)}
                </TableCell>
              )}
              {dataCells(row)}
              {renderActions(row)}
            </>
          )}
        </TableRow>
      ))}
    </TableBody>
  );
}
